import { Box, Typography, useTheme } from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import {
  Trans,
  useLocalPriceDisplay,
  useTranslation,
} from "@outschool/localization";
import React from "react";

import { useMobileCDPGroupExperiment } from "../hooks/experiments/useMobileCDPGroupExperiment";
import { CoinIcon } from "../Subscriptions";
import { creditsPerLearnerPerMeeting } from "@outschool/business-rules";

export default function MobileActivityCredits({
  activity,
}: {
  activity: Pick<
    Activity,
    | "allows_recurring_payment"
    | "isClub"
    | "is_ongoing_weekly"
    | "duration_weeks"
    | "hasTeacherSchedule"
    | "price_cents"
    | "weekly_meetings"
    | "isFlexSchedule"
    | "size_max"
    | "isOneTime"
    | "isSelfPaced"
    | "priceCredits"
    | "perWeeklyCourseCredits"
  >;
}) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\MobileActivityCredits"
  );

  const enableMobileCDPGroup1Exp = useMobileCDPGroupExperiment();
  const theme = useTheme();
  const { makePerMeetingPriceCreditsText } = useLocalPriceDisplay();

  const flexSx = { flexDirection: "row", gap: 4, alignItems: "end" };
  let pricePrefix = "";

  if (enableMobileCDPGroup1Exp) {
    flexSx.alignItems = "center;";
    pricePrefix = "charged ";
  }

  if (activity.hasTeacherSchedule) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perMeetingPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}per session
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.isOneTime) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perMeetingPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}per class
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.isClub) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perMonthPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}monthly
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.is_ongoing_weekly && activity.weekly_meetings === 1) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perMeetingPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}weekly
          </Typography>
        </Box>
      </Trans>
    );
  } else if (
    activity.is_ongoing_weekly &&
    (activity.weekly_meetings || 0) > 1
  ) {
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <CoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="h6">
                {{ weeklyPrice: activity.priceCredits }}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {" "}
              {pricePrefix}per week
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="body2">
              (
              {{
                perMeetingPriceCredits:
                  makePerMeetingPriceCreditsText(activity),
              }}{" "}
              {pricePrefix}per class)
            </Typography>
          </Box>
        </Box>
      </Trans>
    );
  } else if (activity.isSelfPaced) {
    if (activity.duration_weeks === 1) {
      return (
        <Trans t={t}>
          <Box flex sx={{ flexDirection: "column" }}>
            <Box flex sx={flexSx}>
              <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
                <CoinIcon
                  fillColor={theme.palette.warning.main}
                  strokeColor={theme.palette.warning[700]}
                />
                <Typography variant="h6">
                  {{ perCourseCredits: activity.priceCredits }}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ marginBottom: 1 }}>
                {" "}
                {pricePrefix}for all content
              </Typography>
            </Box>
          </Box>
        </Trans>
      );
    }
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <CoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="h6">
                {{
                  perWeeklyCourseCredits: !!activity.weekly_meetings
                    ? creditsPerLearnerPerMeeting(activity) *
                      activity.weekly_meetings
                    : creditsPerLearnerPerMeeting(activity),
                }}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {" "}
              {pricePrefix}weekly
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">or </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {{ perCourseCredits: activity.priceCredits }}
            </Typography>
            <Typography variant="body2">
              {" "}
              {pricePrefix}for all content
            </Typography>
          </Box>
        </Box>
      </Trans>
    );
  } else if (!activity.allows_recurring_payment) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <CoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="h6">
              {{ perCourseCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {" "}
            {pricePrefix}for{" "}
            {{
              classes:
                (activity.duration_weeks || 0) *
                (activity.weekly_meetings || 0),
            }}{" "}
            classes
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.allows_recurring_payment) {
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <CoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="h6">
                {{
                  perWeeklyCourseCredits: !!activity.weekly_meetings
                    ? creditsPerLearnerPerMeeting(activity) *
                      activity.weekly_meetings
                    : creditsPerLearnerPerMeeting(activity),
                }}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {" "}
              {pricePrefix}weekly
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">or </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {{ perCourseCredits: activity.priceCredits }}
            </Typography>
            <Typography variant="body2">
              {" "}
              {pricePrefix}for{" "}
              {{
                classes:
                  (activity.duration_weeks || 0) *
                  (activity.weekly_meetings || 0),
              }}{" "}
              classes
            </Typography>
          </Box>
        </Box>
      </Trans>
    );
  } else {
    return null;
  }
}
